import { TASTING_MAX_PHOTOS_PER_NOTE } from '@recipes/contracts';
import { Badge, Button, Input, Label, StarRating, Textarea } from '@recipes/ui';
import { Camera, ImagePlus, Mic, Trash2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig, useFeature, useProfile } from '@/features/shared/hooks';
import { usePushToTalk } from '@/features/speech/use-push-to-talk';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { currentLocale } from '@/lib/i18n';
import { getPlatform, type PhotoMetadata } from '@/lib/platform';
import type { DishDraft } from './dish-draft';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

/**
 * Photo, dish name, tasting notes (with dictation), rating and flavor tags of one dish.
 * Photo metadata (EXIF position and time) is read on the device – the server strips EXIF.
 */
export function DishFields({
  value,
  onChange,
  onPhotoMetadata,
  allowPhotos = true,
  existingPhotoCount = 0,
}: {
  value: DishDraft;
  onChange: (next: DishDraft) => void;
  /** Called with the metadata of newly added photos (position/time proposals). */
  onPhotoMetadata?: (metadata: PhotoMetadata) => void;
  allowPhotos?: boolean;
  /** Photos the dish already has on the server (edit dialog); they count towards the limit. */
  existingPhotoCount?: number;
}) {
  const { t } = useTranslation();
  const ids = { dish: useId(), notes: useId(), tag: useId() };
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const latest = useRef(value);
  latest.current = value;
  const [tagDraft, setTagDraft] = useState('');

  const speechEnabled = useFeature('speech');
  const online = useOnlineStatus();
  const { data: config } = useConfig();
  const { data: profile } = useProfile();
  const ptt = usePushToTalk({
    language: currentLocale(),
    preferServerTranscription: profile?.speechPreferences.preferServerTranscription ?? false,
    serverTranscriptionAvailable:
      online && speechEnabled && (config?.speech.serverTranscription ?? false),
    context: 'dictation',
    onResult: (transcript) => {
      const text = transcript.trim();
      if (!text) return;
      const current = latest.current;
      onChange({ ...current, notes: current.notes ? `${current.notes.trimEnd()} ${text}` : text });
    },
  });
  const dictation =
    speechEnabled && (profile?.speechPreferences.inputEnabled ?? true) && ptt.adapter;

  // Object URLs are released when a photo leaves the form or the form unmounts.
  const urls = useRef(new Set<string>());
  useEffect(() => {
    const current = new Set(value.photos.map((p) => p.previewUrl));
    for (const url of urls.current) if (!current.has(url)) URL.revokeObjectURL(url);
    urls.current = current;
  }, [value.photos]);
  useEffect(() => () => urls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const room = TASTING_MAX_PHOTOS_PER_NOTE - existingPhotoCount - value.photos.length;

  const addPicked = (picked: Array<{ file: File; metadata: PhotoMetadata }>) => {
    const next = picked.slice(0, Math.max(0, room)).map(({ file, metadata }) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      metadata,
    }));
    if (!next.length) return;
    onChange({ ...latest.current, photos: [...latest.current.photos, ...next] });
    const withData = next.find((p) => p.metadata.position || p.metadata.takenAt);
    if (withData) onPhotoMetadata?.(withData.metadata);
  };

  /**
   * `files` is a plain array on purpose: the input is emptied right after the change event so
   * that the same photo can be chosen again, and in some browsers that empties the FileList too –
   * whoever reads it after an `await` finds nothing.
   */
  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    const platform = await getPlatform();
    addPicked(
      await Promise.all(
        files.map(async (file) => ({
          file,
          metadata: await platform.readPhotoMetadata(file),
        })),
      ),
    );
  };

  const pick = async (source: 'camera' | 'gallery') => {
    const platform = await getPlatform();
    const native = await platform.pickPhotos(source);
    if (native) addPicked(native);
    else (source === 'camera' ? cameraRef : galleryRef).current?.click();
  };

  const addTag = () => {
    const tag = tagDraft.trim().toLowerCase().slice(0, 40);
    setTagDraft('');
    if (!tag || value.flavorTags.includes(tag) || value.flavorTags.length >= 20) return;
    onChange({ ...value, flavorTags: [...value.flavorTags, tag] });
  };

  return (
    <div className="space-y-4">
      {allowPhotos && (
        <section aria-label={t('tasting.dish.photos')}>
          <input
            ref={galleryRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            data-testid="tasting-photo-input"
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
          {value.photos.length > 0 && (
            <ul className="mb-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {value.photos.map((photo, index) => (
                <li key={photo.id} className="relative">
                  <img
                    src={photo.previewUrl}
                    alt={t('tasting.dish.photoAlt', { index: index + 1 })}
                    className="aspect-square w-full rounded-md object-cover"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute right-1 top-1 size-9"
                    aria-label={t('tasting.dish.removePhoto', { index: index + 1 })}
                    onClick={() =>
                      onChange({ ...value, photos: value.photos.filter((p) => p.id !== photo.id) })
                    }
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="lg"
              disabled={room <= 0}
              onClick={() => void pick('camera')}
              data-testid="tasting-camera"
            >
              <Camera aria-hidden /> {t('tasting.dish.takePhoto')}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="outline"
              disabled={room <= 0}
              onClick={() => void pick('gallery')}
            >
              <ImagePlus aria-hidden /> {t('tasting.dish.pickPhoto')}
            </Button>
          </div>
        </section>
      )}

      <div>
        <Label htmlFor={ids.dish}>{t('tasting.dish.name')}</Label>
        <Input
          id={ids.dish}
          className="mt-1"
          value={value.dishName}
          maxLength={200}
          placeholder={t('tasting.dish.namePlaceholder')}
          onChange={(e) => onChange({ ...value, dishName: e.target.value })}
          data-testid="tasting-dish-name"
        />
      </div>

      <div>
        <div className="flex items-end justify-between gap-2">
          <Label htmlFor={ids.notes}>{t('tasting.dish.notes')}</Label>
          {dictation && (
            <Button
              type="button"
              size="sm"
              variant={ptt.state === 'listening' ? 'default' : 'outline'}
              aria-pressed={ptt.state === 'listening'}
              onPointerDown={() => ptt.start()}
              onPointerUp={() => ptt.stop()}
              onPointerLeave={() => ptt.state === 'listening' && ptt.stop()}
              onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && !e.repeat && ptt.start()}
              onKeyUp={(e) => (e.key === ' ' || e.key === 'Enter') && ptt.stop()}
            >
              <Mic aria-hidden />
              {ptt.state === 'listening' ? t('tasting.dish.listening') : t('tasting.dish.dictate')}
            </Button>
          )}
        </div>
        <Textarea
          id={ids.notes}
          className="mt-1"
          rows={4}
          value={value.notes}
          maxLength={4000}
          placeholder={t('tasting.dish.notesPlaceholder')}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
          data-testid="tasting-notes"
        />
        {ptt.state === 'listening' && ptt.transcript && (
          <p className="mt-1 text-xs text-muted-foreground" aria-live="polite">
            {ptt.transcript}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">{t('tasting.dish.rating')}</span>
        <StarRating
          value={value.rating}
          onChange={(rating) => onChange({ ...value, rating: rating ? Math.round(rating) : null })}
          label={t('tasting.dish.rating')}
        />
      </div>

      <div>
        <Label htmlFor={ids.tag}>{t('tasting.dish.flavorTags')}</Label>
        <div className="mt-1 flex gap-2">
          <Input
            id={ids.tag}
            value={tagDraft}
            maxLength={40}
            placeholder={t('tasting.dish.flavorTagsPlaceholder')}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addTag();
              }
            }}
          />
          <Button type="button" variant="outline" onClick={addTag} disabled={!tagDraft.trim()}>
            {t('tasting.dish.addTag')}
          </Button>
        </div>
        {value.flavorTags.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-2">
            {value.flavorTags.map((tag) => (
              <li key={tag}>
                <Badge variant="secondary" className="gap-1">
                  {tag}
                  <button
                    type="button"
                    className="-mr-1 rounded-full p-1"
                    aria-label={t('tasting.dish.removeTag', { tag })}
                    onClick={() =>
                      onChange({ ...value, flavorTags: value.flavorTags.filter((x) => x !== tag) })
                    }
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
